import React from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import "./loadingSpinner.css";

// Full Page Loading - covers whole page, no overlay
const FullPageLoadingSpinner = () => {
  const spinnerContent = (
    <div className="loading-container loading-fullpage">
      <div className="loading-content">
        <Spinner animation="border" role="status" className="loading-spinner" />
        <div className="loading-text">
          <span className="dot-animation">Loading</span>
        </div>
      </div>
    </div>
  );

  return createPortal(spinnerContent, document.body);
};

// Masked Loading - covers whole page with 40% light transparent mask
const MaskedLoadingSpinner = () => {
  const spinnerContent = (
    <div className="loading-container loading-masked">
      <div className="loading-content">
        <Spinner animation="border" role="status" className="loading-spinner" />
        <div className="loading-text">
          <span className="dot-animation">Loading</span>
        </div>
      </div>
    </div>
  );

  return createPortal(spinnerContent, document.body);
};

// Partial Loading - shows loading inside specific container
export const PartialLoadingSpinner = ({ containerId, className = "" }) => {
  const container = containerId ? document.getElementById(containerId) : null;

  const spinnerContent = (
    <div className={`loading-container loading-partial ${className}`}>
      <div className="loading-content">
        <Spinner animation="border" role="status" className="loading-spinner" />
        <div className="loading-text">
          <span className="dot-animation">Loading</span>
        </div>
      </div>
    </div>
  );

  // If container specified and exists, render inside it
  if (container) {
    return createPortal(spinnerContent, container);
  }

  // Otherwise render inline
  return spinnerContent;
};

// Main LoadingSpinner component that handles all types
const LoadingSpinner = ({
  overlayOpacity,
  usePortal = true,
  type = "legacy",
  containerId,
  className,
}) => {
  // Handle legacy usage for backward compatibility
  if (type === "legacy") {
    const backgroundColor =
      typeof overlayOpacity === "number"
        ? `rgba(248,249,250,${overlayOpacity})`
        : undefined;

    const spinnerContent = (
      <div
        className="loading-container"
        style={backgroundColor ? { backgroundColor } : {}}
      >
        <div className="loading-content">
          <Spinner
            animation="border"
            role="status"
            className="loading-spinner"
          />
          <div className="loading-text">
            <span className="dot-animation">Loading</span>
          </div>
        </div>
      </div>
    );

    if (usePortal && typeof document !== "undefined") {
      return createPortal(spinnerContent, document.body);
    }
    return spinnerContent;
  }

  // Handle new types
  switch (type) {
    case "fullPage":
      return <FullPageLoadingSpinner />;
    case "masked":
      return <MaskedLoadingSpinner />;
    case "partial":
      return (
        <PartialLoadingSpinner
          containerId={containerId}
          className={className}
        />
      );
    default:
      return null;
  }
};

export default LoadingSpinner;
